import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import cytoscape from '../../../../dependencies/cytoscape.js/build/cytoscape.esm.min.js';
import cytoscapeDagre from 'cytoscape-dagre';
import {
  compileProcessGraph,
  type RawProcessNode,
  type ProcessGraphCompileResult,
} from './utils/processGraph';
import {
  applyEdgeStyleSmooth,
  applyNodeStyleSmooth,
  type CytoscapeStyle,
} from './utils/cytoscapeVisual';
import { panElementIntoView } from './utils/cytoscapeViewport';
import { useConfig } from '../../../../hooks/useConfig';
import type {
  AssignedSlot,
  ProcessNodeStatus,
  ProcessRuntimeState,
  TaskPhase,
} from './types';

// 注册 dagre 布局插件（agent-runtime 范围内只注册一次）
let dagreRegistered = false;
/** 角色卡片背景图固定铺满节点，内部头像和文字由 SVG 自己控制。 */
const ROLE_ICON_SIZE = '100%';
/** 角色卡片背景图始终居中，避免缩放时产生偏移。 */
const ROLE_ICON_LEFT = '50%';
/** 过程图角色卡片真实头像尺寸（会随画布缩放同比例变化）。 */
const ROLE_AVATAR_SIZE = 34;
const ROLE_AVATAR_LEFT = 13;
const PROCESS_OVERLAY_SYNC_DEBUG = false;
/** demo 头像映射：姓名 -> public/static-files/testavatar 下的文件名。 */
const PERSON_AVATAR_DEMO_FILE_MAP: Record<string, string> = {
  邓晓彬: 'xb.png',
  郭俊玺: 'gz.png',
  黄忠鸿: 'hzh.png',
  汤大伟: 'dw.png',
  郭芸溪: 'yx.png',
  杨柳: 'yl.png',
  吴磊: 'xb.png',
  王浩: 'yx.png',
  陈杰: 'dw.png',
  杨欣: 'yl.png',
};

function ensureDagreRegistered() {
  if (!dagreRegistered) {
    cytoscapeDagre(cytoscape);
    dagreRegistered = true;
  }
}

interface ProcessDiagramProps {
  /** 原始 process JSON 字符串（若改变则重新编译整张图） */
  processJson: string;
  /** 当前阶段，控制是否展示结果节点、动画等 */
  phase: TaskPhase;
  /** 过程节点的运行时状态表：processNodeId -> 状态 */
  processStates: Map<string, ProcessRuntimeState>;
  suppressMotion?: boolean;
  /** 暴露编译结果给上层（name → id 映射等） */
  onCompiled?: (graph: ProcessGraphCompileResult) => void;
  /** 第四步全部完成后，整图完成最终视口缩放动画的回调。 */
  onDoneViewportSettled?: () => void;
}

/** 第四步结束后，把整张运行图平滑收进可视区。 */
const FINAL_VIEWPORT_PADDING = 56;
const FINAL_VIEWPORT_ANIMATION_MS = 900;

function unwrapProcessRoot(value: unknown): RawProcessNode | null {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as {
    process?: RawProcessNode;
    name?: string;
    processes?: RawProcessNode[];
  };

  if (candidate.process && typeof candidate.process === 'object') {
    return candidate.process;
  }

  if (typeof candidate.name === 'string') {
    return candidate as RawProcessNode;
  }

  return null;
}

/**
 * 过程组装图（第一/三/四步共用）。
 *
 * 采用 cytoscape.js + dagre LR 布局，结构上：
 *   根（过程） → 分组节点 → 叶子节点 → 角色节点 → （运行阶段）结果节点
 * 颜色方案：每一条顶层分支选一个主题色，整个子树共享；叶子用"描边+底色"样式，
 * 分组和根节点用"实心"样式，尽量贴合设计图 3.png / 8.png / 11.png 的观感。
 */
const ProcessDiagram: React.FC<ProcessDiagramProps> = ({
  processJson,
  phase,
  processStates,
  suppressMotion = false,
  onCompiled,
  onDoneViewportSettled,
}) => {
  const { globalConfig } = useConfig();
  const enablePersonAvatarDemo = globalConfig.enablePersonAvatarDemo;
  const containerRef = useRef<HTMLDivElement>(null);
  const cyRef = useRef<cytoscape.Core | null>(null);
  /** 记录上一轮已应用的过程节点状态签名，避免每条新日志都重放旧动画。 */
  const processVisualStatesRef = useRef<Map<string, string>>(new Map());
  /** 记录角色节点上一轮的展示签名，避免角色槽位在无变化时重复闪动。 */
  const roleVisualStatesRef = useRef<Map<string, string>>(new Map());
  /** 记录结果节点上一轮的展示签名，避免结果区域无变化时重复刷新。 */
  const resultVisualStatesRef = useRef<Map<string, string>>(new Map());
  /** 记录当前 cytoscape 实例是否已经把 result 节点注入进去，避免重复 add */
  const resultNodesInjectedRef = useRef(false);
  /** 当前"执行中"过程节点 id 列表，用于驱动 DOM 覆盖层的转圈动画 */
  const [runningNodeIds, setRunningNodeIds] = useState<string[]>([]);
  /** 当前"工作中"角色卡片 id 列表，用于展示人员工作态标识 */
  const [workingRoleIds, setWorkingRoleIds] = useState<string[]>([]);
  /** 仅用于结果 tooltip 在视口变化时重算位置。 */
  const [viewportVersion, setViewportVersion] = useState(0);
  const runningOverlayRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const workingOverlayRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const roleAvatarOverlayRefs = useRef<Map<string, HTMLImageElement>>(
    new Map()
  );
  /** 记录角色头像加载失败的 key（roleId|avatarUrl），避免持续重试导致闪烁。 */
  const [brokenRoleAvatarKeys, setBrokenRoleAvatarKeys] = useState<Set<string>>(
    () => new Set()
  );
  /** 当前被 hover 的结果节点（用于显示完整文本 tooltip） */
  const [hoveredResult, setHoveredResult] = useState<{
    id: string;
    text: string;
    x: number;
    y: number;
  } | null>(null);
  const hoveredResultIdRef = useRef('');
  const overlayDebugLastTsRef = useRef(0);
  /** 结果 tooltip 延迟隐藏定时器，保证鼠标可从节点顺滑移入卡片。 */
  const hideTooltipTimerRef = useRef<number | null>(null);
  /** 最终完成态的整图 fit 动画只执行一次，避免 done 阶段重复抖动。 */
  const doneViewportAnimatedRef = useRef(false);
  /** 最终 fit 动画前等待一帧，确保画布已完成当前轮渲染。 */
  const doneViewportFrameRef = useRef<number | null>(null);

  /** 清理 tooltip 延迟隐藏，避免旧定时器误把当前卡片关掉。 */
  const cancelHideTooltip = () => {
    if (hideTooltipTimerRef.current !== null) {
      window.clearTimeout(hideTooltipTimerRef.current);
      hideTooltipTimerRef.current = null;
    }
  };

  useEffect(() => {
    hoveredResultIdRef.current = hoveredResult?.id || '';
  }, [hoveredResult]);

  /** tooltip 采用短暂延迟隐藏，给鼠标移入卡片留出过渡时间。 */
  const scheduleHideTooltip = (resultId?: string) => {
    cancelHideTooltip();
    hideTooltipTimerRef.current = window.setTimeout(() => {
      setHoveredResult(prev => {
        if (!prev) return prev;
        if (resultId && prev.id !== resultId) return prev;
        return null;
      });
      hideTooltipTimerRef.current = null;
    }, 120);
  };

  // 缓存编译结果：processJson 不变则不重新编译
  const compiled = useMemo<ProcessGraphCompileResult | null>(() => {
    if (!processJson) return null;
    try {
      const parsed = JSON.parse(processJson) as unknown;
      const raw = unwrapProcessRoot(parsed);
      if (!raw) {
        console.error('[ProcessDiagram] processJson 结构不符合预期', parsed);
        return null;
      }
      return compileProcessGraph(raw);
    } catch (err) {
      console.error('[ProcessDiagram] 解析 processJson 失败', err);
      return null;
    }
  }, [processJson]);

  // 编译结果变化时回调上层（让父组件持有 name→id 映射）
  useEffect(() => {
    if (compiled && onCompiled) onCompiled(compiled);
  }, [compiled, onCompiled]);

  // 初始化 cytoscape 实例
  useEffect(() => {
    if (!containerRef.current || !compiled) return;
    ensureDagreRegistered();

    const cy = cytoscape({
      container: containerRef.current,
      elements: compiled.elements,
      style: buildStylesheet(enablePersonAvatarDemo),
      layout: buildLayoutOptions(),
      userZoomingEnabled: true,
      userPanningEnabled: true,
      autoungrabify: true,
      boxSelectionEnabled: false,
      minZoom: 0.2,
      maxZoom: 2.5,
      wheelSensitivity: 0.2,
    });
    cyRef.current = cy;
    resultNodesInjectedRef.current = false;
    processVisualStatesRef.current = new Map();
    roleVisualStatesRef.current = new Map();
    resultVisualStatesRef.current = new Map();
    setBrokenRoleAvatarKeys(new Set());

    // rAF 合并视口事件，让 overlay 位置跟随但不抖动。
    // 注意不要监听 render：动画/样式变化会高频触发 render，导致 React 连续重渲染，
    // 在步骤 3/4 日志密集与结果节点出现时会挤占滚轮缩放响应。
    let rafId: number | null = null;
    const onViewport = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        if (hoveredResultIdRef.current) {
          setViewportVersion(v => v + 1);
        }
      });
    };
    (cy as any).on('pan zoom resize layoutstop', onViewport);

    // 结果节点悬停时通过 DOM tooltip 显示完整文本
    const onMouseover = (evt: any) => {
      const target = evt.target;
      if (!target || !target.isNode || !target.isNode()) return;
      if (!target.hasClass('result-node')) return;
      const full = (target.data('fullResult') as string) || '';
      if (!full) return;
      cancelHideTooltip();
      const bb = target.renderedBoundingBox();
      setHoveredResult({
        id: target.id(),
        text: full,
        x: (bb.x1 + bb.x2) / 2,
        y: bb.y1,
      });
    };
    const onMouseout = (evt: any) => {
      const target = evt.target;
      if (!target || !target.isNode || !target.isNode()) return;
      if (!target.hasClass('result-node')) return;
      scheduleHideTooltip(target.id());
    };
    (cy as any).on('mouseover', 'node', onMouseover);
    (cy as any).on('mouseout', 'node', onMouseout);

    return () => {
      (cy as any).off('pan zoom resize layoutstop', onViewport);
      (cy as any).off('mouseover', 'node', onMouseover);
      (cy as any).off('mouseout', 'node', onMouseout);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      if (doneViewportFrameRef.current !== null) {
        window.cancelAnimationFrame(doneViewportFrameRef.current);
        doneViewportFrameRef.current = null;
      }
      cancelHideTooltip();
      cy.destroy();
      cyRef.current = null;
      processVisualStatesRef.current = new Map();
      roleVisualStatesRef.current = new Map();
      resultVisualStatesRef.current = new Map();
      doneViewportAnimatedRef.current = false;
    };
  }, [compiled, enablePersonAvatarDemo]);

  useEffect(() => () => cancelHideTooltip(), []);

  /** 完成态执行一次平滑缩放，让最终全图完整落在可视范围内。 */
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !compiled) return;

    if (suppressMotion) {
      doneViewportAnimatedRef.current = false;
      if (doneViewportFrameRef.current !== null) {
        window.cancelAnimationFrame(doneViewportFrameRef.current);
        doneViewportFrameRef.current = null;
      }
      return;
    }

    if (phase !== 'done') {
      doneViewportAnimatedRef.current = false;
      if (doneViewportFrameRef.current !== null) {
        window.cancelAnimationFrame(doneViewportFrameRef.current);
        doneViewportFrameRef.current = null;
      }
      return;
    }

    if (doneViewportAnimatedRef.current) return;
    doneViewportAnimatedRef.current = true;

    const scheduleFinalViewportFit = () => {
      doneViewportFrameRef.current = window.requestAnimationFrame(() => {
        doneViewportFrameRef.current = null;
        (cy as any).resize();
        const elements = cy.elements();
        if (!elements || elements.empty()) {
          onDoneViewportSettled?.();
          return;
        }

        const targetViewport = cy.getFitViewport(
          elements,
          FINAL_VIEWPORT_PADDING
        ) as
          | {
              zoom: number;
              pan: { x: number; y: number };
            }
          | undefined;

        if (!targetViewport?.pan || !Number.isFinite(targetViewport.zoom)) {
          onDoneViewportSettled?.();
          return;
        }

        const currentZoom = cy.zoom();
        const currentPan = cy.pan();
        const zoomChanged = Math.abs(currentZoom - targetViewport.zoom) > 0.01;
        const panChanged =
          Math.abs(currentPan.x - targetViewport.pan.x) > 1 ||
          Math.abs(currentPan.y - targetViewport.pan.y) > 1;

        if (!zoomChanged && !panChanged) {
          onDoneViewportSettled?.();
          return;
        }

        try {
          cy.animate(
            {
              fit: {
                eles: elements,
                padding: FINAL_VIEWPORT_PADDING,
              },
            },
            {
              duration: FINAL_VIEWPORT_ANIMATION_MS,
              easing: 'ease-in-out-cubic',
              complete: () => {
                onDoneViewportSettled?.();
              },
            }
          );
        } catch (err) {
          console.warn('[ProcessDiagram] final viewport animation failed', err);
          cy.fit(elements, FINAL_VIEWPORT_PADDING);
          onDoneViewportSettled?.();
        }
      });
    };

    if (!resultNodesInjectedRef.current) {
      (cy as any).add(compiled.resultElements as any);
      resultNodesInjectedRef.current = true;
      const layout = cy.layout(buildLayoutOptions({ fit: false }));
      layout.one('layoutstop', () => {
        setViewportVersion(v => v + 1);
        scheduleFinalViewportFit();
      });
      layout.run();
      setViewportVersion(v => v + 1);
      return () => {
        if (doneViewportFrameRef.current !== null) {
          window.cancelAnimationFrame(doneViewportFrameRef.current);
          doneViewportFrameRef.current = null;
        }
      };
    }

    scheduleFinalViewportFit();

    return () => {
      if (doneViewportFrameRef.current !== null) {
        window.cancelAnimationFrame(doneViewportFrameRef.current);
        doneViewportFrameRef.current = null;
      }
    };
  }, [compiled, onDoneViewportSettled, phase, suppressMotion]);

  // 阶段变化：进入运行阶段时补齐 result 节点并重新布局
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !compiled) return;

    const shouldShowResult = phase === 'running' || phase === 'done';

    if (shouldShowResult && !resultNodesInjectedRef.current) {
      (cy as any).add(compiled.resultElements as any);
      resultNodesInjectedRef.current = true;
      // 第四步只重新排布新增结果节点，不重新 fit，保持第三步角色匹配时的缩放比例。
      cy.layout(buildLayoutOptions({ fit: false })).run();
      setViewportVersion(v => v + 1);
      return;
    }

    if (!shouldShowResult && resultNodesInjectedRef.current) {
      compiled.resultElements.forEach(element => {
        const id = String(element.data.id || '');
        if (!id) return;
        const ele = cy.getElementById(id);
        if (ele && !ele.empty()) {
          ele.remove();
        }
      });
      resultNodesInjectedRef.current = false;
      cy.layout(buildLayoutOptions({ fit: false })).run();
      setViewportVersion(v => v + 1);
    }
  }, [phase, compiled, suppressMotion]);

  // 运行时状态：把 processStates 同步到 cytoscape 节点 class/label 上
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !compiled) return;

    const currentRunningIds: string[] = [];
    const currentWorkingRoleIds: string[] = [];
    let pendingFocusElement: any = null;
    const nextProcessVisualStates = new Map(processVisualStatesRef.current);
    const nextRoleVisualStates = new Map(roleVisualStatesRef.current);
    const nextResultVisualStates = new Map(resultVisualStatesRef.current);

    nextProcessVisualStates.forEach((_signature, processId) => {
      if (processStates.has(processId)) return;
      const node = cy.getElementById(processId);
      if (node && !node.empty()) {
        applyStatusClass(node as any, 'pending', !suppressMotion);
        applyIncomingEdgeState(node as any, false, !suppressMotion);
      }
      nextProcessVisualStates.delete(processId);

      const roleIds = compiled.processToRoleIds.get(processId) || [];
      roleIds.forEach(roleId => {
        const roleNode = cy.getElementById(roleId);
        if (roleNode && !roleNode.empty()) {
          roleNode.data('label', '');
          roleNode.data('displayName', roleNode.data('roleName') as string);
          roleNode.data('displayRole', '');
          roleNode.data('personName', '');
          roleNode.data('avatarUrl', '');
          applyRolePending(roleNode as any, enablePersonAvatarDemo);
          applyRoleEdgeLabel(roleNode as any, false);
          applyIncomingEdgeState(roleNode as any, false, !suppressMotion);
        }
        nextRoleVisualStates.delete(roleId);
      });

      const resultId = compiled.processToResultId.get(processId);
      if (!resultId) return;
      const resultNode = cy.getElementById(resultId);
      if (resultNode && !resultNode.empty()) {
        resultNode.data('label', '—');
        resultNode.data('fullResult', '');
        resultNode.removeClass('filled');
        applyIncomingEdgeState(resultNode as any, false, !suppressMotion);
      }
      nextResultVisualStates.delete(resultId);
    });

    processStates.forEach((state, processId) => {
      const processData = compiled.processNodeMap.get(processId);
      const node = cy.getElementById(processId);
      if (!node || node.empty()) {
        console.warn('[ProcessDiagram] node not found for id=', processId);
        return;
      }
      // 过程主节点的配色只由 status 决定；不要把 slots/result 算进签名，
      // 否则后续角色匹配或结果写入时会把主节点无意义地重新动画一遍。
      const processSignature = state.status;
      if (nextProcessVisualStates.get(processId) !== processSignature) {
        applyStatusClass(node as any, state.status, !suppressMotion);
        // 连线默认保持灰色；仅当目标节点真正进入激活态后再同步点亮。
        applyIncomingEdgeState(
          node as any,
          state.status !== 'pending',
          !suppressMotion
        );
        if (!suppressMotion) {
          pendingFocusElement = node;
        }
        nextProcessVisualStates.set(processId, processSignature);
      }
      if (
        state.status === 'running' &&
        (phase === 'running' || phase === 'done') &&
        !processData?.isRoot
      ) {
        currentRunningIds.push(processId);
      }

      // 角色节点：根据 slots 更新 label/class
      const roleIds = compiled.processToRoleIds.get(processId) || [];
      roleIds.forEach((roleId, idx) => {
        const roleNode = cy.getElementById(roleId);
        if (!roleNode || roleNode.empty()) return;
        const slot: AssignedSlot | undefined = state.slots[idx];
        const roleName = roleNode.data('roleName') as string;
        if (slot?.isWorking) currentWorkingRoleIds.push(roleId);
        const roleSignature = [
          state.status,
          roleName,
          slot?.role || '',
          slot?.person?.id || slot?.person?.name || '',
          slot?.person?.avatarUrl || '',
          slot?.isWorking ? 'working' : 'idle',
        ].join('|');
        if (nextRoleVisualStates.get(roleId) === roleSignature) return;
        if (slot && slot.person) {
          roleNode.data('label', '');
          roleNode.data('displayName', slot.person.name);
          roleNode.data('displayRole', slot.role || roleName);
          roleNode.data('personName', slot.person.name);
          roleNode.data('avatarUrl', (slot.person.avatarUrl || '').trim());
          applyRoleFilled(
            roleNode as any,
            !!slot.isWorking,
            enablePersonAvatarDemo,
            !suppressMotion
          );
          applyRoleEdgeLabel(roleNode as any, true);
          applyIncomingEdgeState(roleNode as any, true, !suppressMotion);
          if (!suppressMotion) {
            pendingFocusElement = roleNode;
          }
        } else if (state.status !== 'pending') {
          // 至少进入过初始化后，角色槽位从灰色变成"待派发"
          roleNode.data('label', '');
          roleNode.data('displayName', roleName);
          roleNode.data('displayRole', '');
          roleNode.data('personName', '');
          roleNode.data('avatarUrl', '');
          applyRoleActivated(
            roleNode as any,
            enablePersonAvatarDemo,
            !suppressMotion
          );
          applyRoleEdgeLabel(roleNode as any, false);
          applyIncomingEdgeState(roleNode as any, true, !suppressMotion);
          if (!suppressMotion) {
            pendingFocusElement = roleNode;
          }
        } else {
          roleNode.data('label', '');
          roleNode.data('displayName', roleName);
          roleNode.data('displayRole', '');
          roleNode.data('personName', '');
          roleNode.data('avatarUrl', '');
          applyRolePending(roleNode as any, enablePersonAvatarDemo);
          applyRoleEdgeLabel(roleNode as any, false);
          applyIncomingEdgeState(roleNode as any, false, !suppressMotion);
        }
        nextRoleVisualStates.set(roleId, roleSignature);
      });

      // 结果节点：根据 result 内容写入（仅第四步之后才存在）
      const resultId = compiled.processToResultId.get(processId);
      if (resultId) {
        const resultNode = cy.getElementById(resultId);
        if (resultNode && !resultNode.empty()) {
          const text = (state.result || '').trim();
          const resultSignature = text;
          if (nextResultVisualStates.get(resultId) === resultSignature) return;
          if (text) {
            resultNode.data('label', formatResultPreview(text));
            resultNode.data('fullResult', text);
            resultNode.addClass('filled');
            applyIncomingEdgeState(resultNode as any, true, !suppressMotion);
            if (!suppressMotion) {
              pendingFocusElement = resultNode;
            }
          } else {
            resultNode.data('label', '—');
            resultNode.data('fullResult', '');
            resultNode.removeClass('filled');
            applyIncomingEdgeState(resultNode as any, false, !suppressMotion);
          }
          nextResultVisualStates.set(resultId, resultSignature);
        }
      }
    });

    processVisualStatesRef.current = nextProcessVisualStates;
    roleVisualStatesRef.current = nextRoleVisualStates;
    resultVisualStatesRef.current = nextResultVisualStates;
    setRunningNodeIds(currentRunningIds);
    setWorkingRoleIds(currentWorkingRoleIds);
    if (!suppressMotion) {
      panElementIntoView(cy, pendingFocusElement);
    }
  }, [processStates, compiled, phase, enablePersonAvatarDemo, suppressMotion]);

  // 运行时高亮节点 —— 只保留渲染所需静态信息，位置走 DOM 直写同步
  const runningOverlays = useMemo(() => {
    if (suppressMotion) return [];
    if (phase !== 'running' && phase !== 'done') return [];
    return runningNodeIds
      .map(id => {
        return {
          id,
          color:
            (compiled?.processNodeMap.get(id)?.themeColor as string) ||
            '#10b981',
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      color: string;
    }>;
  }, [runningNodeIds, phase, compiled, suppressMotion]);

  /** 人员卡片工作中标识 —— 独立 DOM 层，避免频繁重绘 SVG 背景图。 */
  const workingRoleOverlays = useMemo(() => {
    if (suppressMotion) return [];
    return workingRoleIds
      .map(id => {
        return {
          id,
        };
      })
      .filter(Boolean) as Array<{
      id: string;
    }>;
  }, [workingRoleIds, suppressMotion]);

  /** 已匹配角色卡片真实头像 overlay：位置走 DOM 直写同步。 */
  const roleAvatarOverlays = useMemo(() => {
    const cy = cyRef.current;
    if (!cy || !compiled) return [];

    return Array.from(compiled.roleNodeMap.keys())
      .map(id => {
        const node = cy.getElementById(id);
        if (!node || node.empty() || !node.hasClass('filled')) return null;

        const personName = String(node.data('personName') || '').trim();
        if (!personName) return null;

        const apiAvatarUrl = String(node.data('avatarUrl') || '').trim();
        const demoAvatarUrls = resolvePersonDemoAvatarUrls(
          personName,
          enablePersonAvatarDemo
        );
        const avatarUrl = apiAvatarUrl || demoAvatarUrls[0] || '';
        if (!avatarUrl) return null;

        const avatarKey = `${id}|${avatarUrl}`;
        if (brokenRoleAvatarKeys.has(avatarKey)) return null;

        const fallbackAvatarUrl = apiAvatarUrl
          ? demoAvatarUrls[0] || ''
          : demoAvatarUrls[1] || '';

        return {
          id,
          avatarKey,
          avatarUrl,
          fallbackAvatarUrl,
        };
      })
      .filter(Boolean) as Array<{
      id: string;
      avatarKey: string;
      avatarUrl: string;
      fallbackAvatarUrl: string;
    }>;
  }, [compiled, processStates, brokenRoleAvatarKeys, enablePersonAvatarDemo]);

  /** 角色头像加载失败后，记录当前 key，避免同一资源持续重试。 */
  const handleRoleAvatarLoadError = (roleId: string, avatarUrl: string) => {
    const key = `${roleId}|${avatarUrl}`;
    setBrokenRoleAvatarKeys(prev => {
      if (prev.has(key)) return prev;
      const next = new Set(prev);
      next.add(key);
      return next;
    });
  };

  /** 统一同步过程图所有 overlay 的像素位置，避免依赖 React 每帧重渲染。 */
  useEffect(() => {
    const cy = cyRef.current;
    if (!cy) return;

    let rafId: number | null = null;
    const syncOverlayPositions = () => {
      runningOverlays.forEach(overlay => {
        const element = runningOverlayRefs.current.get(overlay.id);
        if (!element) return;
        const node = cy.getElementById(overlay.id);
        if (!node || node.empty()) {
          element.style.display = 'none';
          return;
        }

        const bb = node.renderedBoundingBox({ includeLabels: false } as any);
        const width = bb.x2 - bb.x1;
        const height = bb.y2 - bb.y1;
        element.style.display = 'block';
        element.style.transform = `translate3d(${bb.x1 - 7}px, ${bb.y1 - 7}px, 0)`;
        element.style.width = `${width + 14}px`;
        element.style.height = `${height + 14}px`;

        const svg = element.querySelector('svg');
        if (svg) {
          svg.setAttribute('viewBox', `0 0 ${width + 14} ${height + 14}`);
        }
        const motion = element.querySelector('animateMotion');
        if (motion) {
          motion.setAttribute('path', buildOrbitPath(width, height, 7));
        }
      });

      workingRoleOverlays.forEach(overlay => {
        const element = workingOverlayRefs.current.get(overlay.id);
        if (!element) return;
        const node = cy.getElementById(overlay.id);
        if (!node || node.empty()) {
          element.style.display = 'none';
          return;
        }

        const zoom = cy.zoom();
        const bb = node.renderedBoundingBox({ includeLabels: false } as any);
        const left = bb.x2 - 8;
        const top = bb.y1 + 6 * zoom;
        element.style.display = 'flex';
        element.style.transform = `translate3d(${left}px, ${top}px, 0) translateX(-100%) scale(${zoom})`;
      });

      roleAvatarOverlays.forEach(overlay => {
        const image = roleAvatarOverlayRefs.current.get(overlay.avatarKey);
        if (!image) return;
        const node = cy.getElementById(overlay.id);
        if (!node || node.empty()) {
          image.style.display = 'none';
          return;
        }

        const bb = node.renderedBoundingBox({ includeLabels: false } as any);
        const renderedHeight = bb.y2 - bb.y1;
        const baseHeight = Number(node.data('nodeHeight')) || 64;
        const scale = baseHeight > 0 ? renderedHeight / baseHeight : 1;
        const size = ROLE_AVATAR_SIZE * scale;
        const left = bb.x1 + ROLE_AVATAR_LEFT * scale;
        const top = bb.y1 + (renderedHeight - size) / 2;

        image.style.display = 'block';
        image.style.transform = `translate3d(${left}px, ${top}px, 0)`;
        image.style.width = `${size}px`;
        image.style.height = `${size}px`;
        image.style.borderWidth = `${Math.max(1, 2 * scale)}px`;
      });

      if (PROCESS_OVERLAY_SYNC_DEBUG) {
        const now = performance.now();
        if (now - overlayDebugLastTsRef.current > 300) {
          overlayDebugLastTsRef.current = now;
          console.debug('[ProcessDiagram] overlay sync tick', {
            runningCount: runningOverlays.length,
            workingCount: workingRoleOverlays.length,
            avatarCount: roleAvatarOverlays.length,
            zoom: cy.zoom(),
            pan: cy.pan(),
          });
        }
      }
    };

    const scheduleSync = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        syncOverlayPositions();
      });
    };

    scheduleSync();
    (cy as any).on('pan zoom resize layoutstop render', scheduleSync);

    return () => {
      (cy as any).off('pan zoom resize layoutstop render', scheduleSync);
      if (rafId !== null) window.cancelAnimationFrame(rafId);
    };
  }, [runningOverlays, workingRoleOverlays, roleAvatarOverlays]);

  /** 根据画布边界动态计算 tooltip 位置，避免靠边节点被遮挡。 */
  const hoveredResultStyle = useMemo<React.CSSProperties | null>(() => {
    if (!hoveredResult) return null;
    const container = containerRef.current;
    const containerWidth = container?.clientWidth || 0;
    const containerHeight = container?.clientHeight || 0;
    const tooltipWidth = Math.min(420, Math.max(280, containerWidth - 32));
    const tooltipMaxHeight = Math.min(360, Math.max(180, containerHeight - 32));
    const left = clamp(
      hoveredResult.x,
      tooltipWidth / 2 + 12,
      Math.max(tooltipWidth / 2 + 12, containerWidth - tooltipWidth / 2 - 12)
    );
    const showOnTop =
      hoveredResult.y > tooltipMaxHeight + 28 ||
      hoveredResult.y > containerHeight - hoveredResult.y;
    const top = showOnTop
      ? Math.max(12, hoveredResult.y - 12)
      : Math.min(containerHeight - 12, hoveredResult.y + 12);

    return {
      left,
      top,
      width: tooltipWidth,
      maxHeight: tooltipMaxHeight,
      transform: showOnTop ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
    };
  }, [hoveredResult, viewportVersion]);

  return (
    <div className="relative h-full w-full overflow-hidden bg-white">
      {/* keyframes 内嵌，避免污染全局 */}
      <style>{`
        @keyframes pdNodeActive { 0%,100%{transform:scale(1);opacity:.72} 50%{transform:scale(1.035);opacity:1} }
        @keyframes pdWorking { 0%,100%{transform:scale(1);box-shadow:0 0 0 0 rgba(34,197,94,.28)} 50%{transform:scale(1.06);box-shadow:0 0 0 6px rgba(34,197,94,0)} }
      `}</style>
      <div ref={containerRef} className="h-full w-full" />

      {/* 执行中节点呼吸高亮：比绕圈绿点更接近常规流程图 active 态。 */}
      {runningOverlays.map(overlay => (
        <div
          key={overlay.id}
          ref={element => {
            if (element) {
              runningOverlayRefs.current.set(overlay.id, element);
            } else {
              runningOverlayRefs.current.delete(overlay.id);
            }
          }}
          className="pointer-events-none absolute"
          style={{
            left: 0,
            top: 0,
            width: 120,
            height: 64,
            borderRadius: 9999,
            transform: 'translate3d(0, 0, 0)',
          }}
        >
          {/* 外层负责位移定位；呼吸动画放到内层，避免 transform 冲突导致回到左上角。 */}
          <div
            className="absolute inset-0"
            style={{
              borderRadius: 9999,
              boxShadow: `0 0 0 6px ${hexWithAlpha(overlay.color, 0.12)}`,
              background: hexWithAlpha(overlay.color, 0.06),
              animation: 'pdNodeActive 1.4s ease-in-out infinite',
            }}
          />
          {/* 绕圈绿点仅在真正运行阶段展示；done（含异常提前完成）只保留外层呼吸圈用于定位。 */}
          {phase === 'running' && (
            <svg
              className="absolute inset-0 h-full w-full overflow-visible"
              viewBox="0 0 120 64"
            >
              <circle r="4.5" fill="#10b981" stroke="#ecfdf5" strokeWidth="1">
                <animateMotion
                  dur="1.8s"
                  repeatCount="indefinite"
                  rotate="auto"
                  path={buildOrbitPath(106, 50, 7)}
                />
              </circle>
            </svg>
          )}
        </div>
      ))}

      {roleAvatarOverlays.map(overlay => (
        <img
          key={overlay.avatarKey}
          ref={image => {
            if (image) {
              roleAvatarOverlayRefs.current.set(overlay.avatarKey, image);
            } else {
              roleAvatarOverlayRefs.current.delete(overlay.avatarKey);
            }
          }}
          src={overlay.avatarUrl}
          alt=""
          className="pointer-events-none absolute rounded-full border-solid border-white object-cover shadow-[0_1px_4px_rgba(15,23,42,0.16)]"
          style={{
            left: 0,
            top: 0,
            width: ROLE_AVATAR_SIZE,
            height: ROLE_AVATAR_SIZE,
            borderWidth: 2,
            aspectRatio: '1 / 1',
            zIndex: 3,
            transform: 'translate3d(0, 0, 0)',
          }}
          data-fallback-src={overlay.fallbackAvatarUrl}
          onError={event => {
            const target = event.currentTarget;
            const fallbackSrc = (target.dataset.fallbackSrc || '').trim();
            if (fallbackSrc && target.getAttribute('src') !== fallbackSrc) {
              target.setAttribute('src', fallbackSrc);
              target.dataset.fallbackSrc = '';
              return;
            }
            handleRoleAvatarLoadError(overlay.id, overlay.avatarUrl);
          }}
        />
      ))}

      {/* 工作中的执行 Agent：用轻量胶囊标识当前人员正在触发执行。 */}
      {workingRoleOverlays.map(overlay => (
        <div
          key={overlay.id}
          ref={element => {
            if (element) {
              workingOverlayRefs.current.set(overlay.id, element);
            } else {
              workingOverlayRefs.current.delete(overlay.id);
            }
          }}
          className="pointer-events-none absolute flex items-center gap-1 rounded-full border border-emerald-200 bg-white/95 px-2 py-0.5 text-[10px] font-medium text-emerald-700 shadow-sm"
          style={{
            // 工作中标识锚定在人员卡片右上区域，并按画布 zoom 同步缩放。
            left: 0,
            top: 0,
            transform: 'translate3d(0, 0, 0) translateX(-100%) scale(1)',
            transformOrigin: 'top right',
          }}
        >
          <span
            className="block h-2 w-2 rounded-full bg-emerald-500"
            style={{ animation: 'pdWorking 1.2s ease-in-out infinite' }}
          />
          工作中
        </div>
      ))}

      {/* 结果节点 hover tooltip —— 展示完整 think 文本 */}
      {hoveredResult && hoveredResultStyle && (
        <div
          className="thin-scrollbar pointer-events-auto absolute z-20 overflow-y-auto rounded-md border border-slate-200 bg-white p-3 text-[11px] leading-relaxed text-slate-700 shadow-lg"
          style={{
            ...hoveredResultStyle,
            whiteSpace: 'pre-wrap',
            overscrollBehavior: 'contain',
          }}
          onMouseEnter={cancelHideTooltip}
          onMouseLeave={() => scheduleHideTooltip(hoveredResult.id)}
          onWheel={event => {
            event.stopPropagation();
            const currentTarget = event.currentTarget;
            if (currentTarget.scrollHeight <= currentTarget.clientHeight) {
              event.preventDefault();
            }
          }}
        >
          {hoveredResult.text}
        </div>
      )}
    </div>
  );
};

export default memo(ProcessDiagram);

// =============================================================
// 样式构造：保持与设计图一致的配色
// =============================================================

function getLayoutSortValue(ele: any): number {
  const rawValue = ele?.data?.('layoutSeq');
  const value = Number(rawValue);
  return Number.isFinite(value) ? value : Number.MAX_SAFE_INTEGER;
}

/** dagre 布局配置 */
function buildLayoutOptions(options: { fit?: boolean } = {}) {
  return {
    name: 'dagre',
    rankDir: 'LR',
    rankSep: 60,
    nodeSep: 22,
    edgeSep: 14,
    // 初始化动画关闭以避免在数据驱动过程中与 pan/zoom 抢占 GPU
    animate: false,
    fit: options.fit ?? true,
    padding: 40,
    sort: (a: any, b: any) => getLayoutSortValue(a) - getLayoutSortValue(b),
  } as any;
}

/**
 * 根据节点状态写入**直接 bypass 样式**。
 *
 * 背景：class 级 stylesheet 规则（哪怕是简单的 `.leaf.activated` + 函数映射）
 * 在 class 动态变化时，cytoscape 内部可能因为 context style cache 或 mapped
 * property 的 refresh 时机问题而不生效（实测日志里 classes 已含 activated 但
 * `background-color` 读到的仍是默认灰）。
 *
 * 改用 `node.style({...})` 写 bypass 样式：这相当于每个节点独立的 inline CSS，
 * 一旦调用即生效，与 class/selector 匹配完全无关。
 *
 * 与此同时仍保留 class（`status-xxx` / `activated`）以便其它特性（如 CSS 调试、
 * 外部 hover 判断等）可以观察到状态。
 */
function applyStatusClass(
  node: any,
  status: ProcessNodeStatus,
  animated = true
) {
  const allStatus = [
    'status-pending',
    'status-initialized',
    'status-structured',
    'status-running',
    'status-completed',
    'status-destroyed',
  ];
  for (const cls of allStatus) node.removeClass(cls);
  node.addClass(`status-${status}`);
  if (status === 'pending') {
    node.removeClass('activated');
  } else {
    node.addClass('activated');
  }

  // ---------- 直接写 bypass 样式 ----------
  const themeColor = (node.data('themeColor') as string) || '#9ca3af';
  const themeSoftBg = (node.data('themeSoftBg') as string) || '#f3f4f6';
  const isRoot = node.hasClass('root');
  const isGroup = node.hasClass('group');
  const isLeaf = node.hasClass('leaf');
  if (status === 'pending') {
    // 还原成默认灰态（清掉之前的 bypass）
    node.removeStyle(
      'background-color border-color color border-width opacity font-size font-weight'
    );
    applyProcessLabelImage(node as any);
    return;
  }

  let nextStyle: CytoscapeStyle | null = null;
  // 激活类的配色：根节点黑色实心；分组节点主题实心；叶子节点浅底+主题描边
  if (isRoot) {
    nextStyle = {
      'background-color': '#111827',
      'border-color': '#111827',
      color: '#ffffff',
      opacity: 1,
    };
  } else if (isGroup) {
    nextStyle = {
      'background-color': themeColor,
      'border-color': themeColor,
      color: '#ffffff',
      opacity: 1,
    };
  } else if (isLeaf) {
    nextStyle = {
      'background-color': themeSoftBg,
      'border-color': themeColor,
      color: themeColor,
      opacity: 1,
    };
  }

  // 过程节点文字和人员/角色卡片一样走 SVG 背景，不再使用 Cytoscape label，避免缩放时文字错位。
  applyProcessLabelImage(node as any);

  // running 态再额外加粗边框；其它状态恢复默认
  if (nextStyle) {
    nextStyle['border-width'] = status === 'running' ? 2.5 : 1.4;
    if (animated) {
      applyNodeStyleSmooth(node, nextStyle, {
        fallbackBorderWidth: status === 'running' ? 2.5 : 1.4,
      });
    } else {
      node.stop(true, false);
      node.style(nextStyle);
    }
  }
}

/** 过程节点文字渲染为 SVG 背景图，和人员/角色卡片保持同一套稳定绘制方式。 */
function applyProcessLabelImage(node: any) {
  /** 根节点比普通过程节点再大一档，和当前设计稿层级保持一致。 */
  const processFontSize = node.data('isRoot') ? 19 : 17;
  node.style({
    label: '',
    'text-opacity': 0,
    'background-image': makeProcessLabelDataUri({
      width: node.data('nodeWidth') || 240,
      height: node.data('nodeHeight') || 76,
      label: node.data('label') || '',
      color: getProcessLabelColor(node),
      fontSize: processFontSize,
    }),
    'background-width': ROLE_ICON_SIZE,
    'background-height': ROLE_ICON_SIZE,
    'background-position-x': ROLE_ICON_LEFT,
    'background-position-y': '50%',
    'background-repeat': 'no-repeat',
  });
  logProcessLabelDebug(node);
}

/** 根据过程节点状态取文字颜色：分组/根激活白字，叶子激活主题色，未激活灰字。 */
function getProcessLabelColor(node: any): string {
  if (!node.hasClass('activated')) return '#9ca3af';
  if (node.hasClass('root') || node.hasClass('group')) return '#ffffff';
  return (node.data('themeColor') as string) || '#9ca3af';
}

const processLabelDebugLogged = new Set<string>();

/** 调试过程节点文字：确认 Cytoscape 原生 label 是否真的被关闭。 */
function logProcessLabelDebug(node: any) {
  const id = String(node?.id?.() || '');
  if (!id || processLabelDebugLogged.has(id)) return;
  processLabelDebugLogged.add(id);
  console.info('[ProcessDiagram] process label debug', {
    id,
    dataLabel: node.data('label'),
    styleLabel: node.style('label'),
    textOpacity: node.style('text-opacity'),
    hasBackgroundImage: Boolean(node.style('background-image')),
  });
}

/** 角色节点：pending 状态，灰图标/灰描边 */
function applyRolePending(node: any, enablePersonAvatarDemo = true) {
  node.removeClass('activated filled');
  node.addClass('status-pending');
  node.removeStyle(
    'background-color border-color color background-image font-weight border-width'
  );
  const roleName = (node.data('roleName') as string) || '';
  node.style({
    'background-image': makeRoleCardDataUri({
      width: node.data('nodeWidth') || 210,
      height: node.data('nodeHeight') || 64,
      name: roleName,
      role: '',
      color: '#9ca3af',
      status: 'pending',
      hideDefaultAvatar: shouldHideRoleDefaultAvatar(
        node.data(),
        enablePersonAvatarDemo
      ),
    }),
    'background-width': ROLE_ICON_SIZE,
    'background-height': ROLE_ICON_SIZE,
    'background-position-x': ROLE_ICON_LEFT,
    'background-position-y': '50%',
    'background-repeat': 'no-repeat',
  });
}

/**
 * 角色节点图标单独直接写入。
 *
 * `background-image` 不参与动画，避免缩放时再次触发异常；
 * 同时保证角色进入激活/已匹配状态后，图标颜色能立即切成分支主题色。
 */
function applyRoleIcon(node: any, color: string) {
  const isFilled = node.hasClass('filled');
  const roleName = (node.data('roleName') as string) || '';
  const displayName = (node.data('displayName') as string) || roleName;
  const displayRole = (node.data('displayRole') as string) || '';
  const agentColor = isFilled ? getAgentAccentColor(node) : color;
  const enablePersonAvatarDemo =
    (node.data('enablePersonAvatarDemo') as boolean) !== false;
  node.style({
    'background-image': makeRoleCardDataUri({
      width: node.data('nodeWidth') || 210,
      height: node.data('nodeHeight') || 64,
      name: displayName,
      role: displayRole,
      color: agentColor,
      status: isFilled ? 'filled' : 'activated',
      hideDefaultAvatar: shouldHideRoleDefaultAvatar(
        node.data(),
        enablePersonAvatarDemo
      ),
    }),
    'background-width': ROLE_ICON_SIZE,
    'background-height': ROLE_ICON_SIZE,
    'background-position-x': ROLE_ICON_LEFT,
    'background-position-y': '50%',
    'background-repeat': 'no-repeat',
  });
}

/** 角色节点：激活态（占位），主题色描边+彩色图标 */
function applyRoleActivated(
  node: any,
  enablePersonAvatarDemo = true,
  animated = true
) {
  node.removeClass('status-pending filled');
  node.addClass('activated');
  node.data('enablePersonAvatarDemo', enablePersonAvatarDemo);
  const themeColor = (node.data('themeColor') as string) || '#9ca3af';
  const nextStyle: CytoscapeStyle = {
    'background-color': '#ffffff',
    'border-color': themeColor,
    color: themeColor,
    'border-width': 1.2,
  };
  if (animated) {
    applyNodeStyleSmooth(node, nextStyle);
  } else {
    node.stop(true, false);
    node.style(nextStyle);
  }
  applyRoleIcon(node, themeColor);
}

/** 角色节点：已匹配数字员工，工作中时再额外加粗边框并增强底色。 */
function applyRoleFilled(
  node: any,
  isWorking = false,
  enablePersonAvatarDemo = true,
  animated = true
) {
  node.removeClass('status-pending');
  node.addClass('activated filled');
  node.data('enablePersonAvatarDemo', enablePersonAvatarDemo);
  const agentColor = getAgentAccentColor(node);
  const agentBg = makeAgentSoftBg(agentColor, isWorking ? 0.16 : 0.08);
  const nextStyle: CytoscapeStyle = {
    'background-color': agentBg,
    'border-color': agentColor,
    color: '#1f2937',
    'font-weight': 'bold',
    'border-width': isWorking ? 2 : 1.2,
  };
  if (animated) {
    applyNodeStyleSmooth(node, nextStyle);
  } else {
    node.stop(true, false);
    node.style(nextStyle);
  }
  applyRoleIcon(node, agentColor);
}

/**
 * 根据目标节点状态同步其入边显隐/着色。
 *
 * 这里同样使用 bypass 样式，避免边线继续受 stylesheet 函数映射影响而在初始时
 * 就被提前着色。默认状态下去掉 bypass，让边回到基础灰色；激活后再按 edge 自身
 * 的 themeColor 立即点亮。
 */
function applyIncomingEdgeState(node: any, active: boolean, animated = true) {
  const incomingEdges = node.incomers('edge');
  if (!incomingEdges || incomingEdges.empty()) return;

  incomingEdges.forEach((edge: any) => {
    if (!active) {
      edge.removeClass('activated');
      edge.removeStyle('line-color target-arrow-color opacity');
      return;
    }

    const themeColor = (edge.data('themeColor') as string) || '#94a3b8';
    edge.addClass('activated');
    const nextStyle: CytoscapeStyle = {
      'line-color': themeColor,
      'target-arrow-color': themeColor,
      opacity: 1,
    };
    if (animated) {
      applyEdgeStyleSmooth(edge, nextStyle);
    } else {
      edge.stop(true, false);
      edge.style(nextStyle);
    }
  });
}

/**
 * 角色边中部文案：
 * - 默认/步骤 1：执行角色
 * - 第 3 步匹配到具体人后：执行Agent
 */
function applyRoleEdgeLabel(node: any, assigned: boolean) {
  const incomingEdges = node.incomers('edge');
  if (!incomingEdges || incomingEdges.empty()) return;

  incomingEdges.forEach((edge: any) => {
    if (!edge.hasClass('role-edge')) return;
    edge.data('lineLabel', assigned ? '执行Agent' : '执行角色');
  });
}

/**
 * 结果节点预览文本：主动硬换行并限制行数。
 *
 * Cytoscape 对连续中文长文本的自动 wrap 不稳定，所以这里先生成 3 行以内的
 * 预览文本，保证文字不会撑出结果节点；完整内容仍通过 hover tooltip 展示。
 */
function formatResultPreview(
  text: string,
  maxLines = 3,
  maxUnits = 34
): string {
  const compact = text.replace(/\s+/g, ' ').trim();
  if (!compact) return '—';

  const lines: string[] = [];
  let current = '';
  let currentUnits = 0;
  let consumed = 0;

  for (const char of compact) {
    const units = estimatePreviewCharUnits(char);
    if (current && currentUnits + units > maxUnits) {
      lines.push(current);
      current = char;
      currentUnits = units;
      if (lines.length === maxLines) break;
    } else {
      current += char;
      currentUnits += units;
    }
    consumed += 1;
  }

  if (lines.length < maxLines && current) {
    lines.push(current);
  }

  if (consumed < compact.length && lines.length > 0) {
    lines[lines.length - 1] = appendPreviewEllipsis(
      lines[lines.length - 1],
      maxUnits
    );
  }

  return lines.join('\n');
}

function appendPreviewEllipsis(text: string, maxUnits: number): string {
  let result = text;
  while (result && measurePreviewTextUnits(`${result}…`) > maxUnits) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function measurePreviewTextUnits(text: string): number {
  let units = 0;
  for (const char of text) {
    units += estimatePreviewCharUnits(char);
  }
  return units;
}

function estimatePreviewCharUnits(char: string): number {
  return /[\u4e00-\u9fa5]/.test(char) ? 2 : 1;
}

/** 生成过程节点文字 SVG，仅负责绘制文字，节点形状/底色仍由 Cytoscape 节点本体负责。 */
function makeProcessLabelDataUri(options: {
  width: number;
  height: number;
  label: string;
  color: string;
  fontSize?: number;
}): string {
  const { width, height, label, color, fontSize = 17 } = options;
  const displayLabel = fitRoleTextToLine(label, width - 36, fontSize);
  const svg = `<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE svg>
  <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>
    <g font-family='system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif'>
      <text x='50%' y='50%' fill='${color}' font-size='${fontSize}' font-weight='700' text-anchor='middle' dominant-baseline='central'>${escapeRoleSvgText(displayLabel)}</text>
    </g>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/**
 * 生成角色 / 已匹配人员卡片 SVG。
 *
 * 步骤 3 里角色节点需要展示两种内容：
 * - 未匹配：角色名
 * - 已匹配：人员姓名 + 角色名
 * 统一绘制为整张背景图，避免 Cytoscape label 与头像图层在缩放时错位。
 */
function makeRoleCardDataUri(options: {
  width: number;
  height: number;
  name: string;
  role: string;
  color: string;
  status: 'pending' | 'activated' | 'filled';
  hideDefaultAvatar?: boolean;
}): string {
  const {
    width,
    height,
    name,
    role,
    color,
    status,
    hideDefaultAvatar = false,
  } = options;
  const muted = status === 'pending';
  const avatarColor = muted ? '#98A2B3' : color;
  const nameColor = muted ? '#98A2B3' : status === 'filled' ? '#344054' : color;
  const roleColor = muted ? '#98A2B3' : '#475467';
  const displayName = fitRoleTextToLine(
    name,
    width - 78,
    status === 'filled' ? 15 : 14
  );
  const displayRole = fitRoleTextToLine(role, width - 78, 12);
  const nameY = role ? 27 : 37;
  const roleSvg = role
    ? `<text x='58' y='45' fill='${roleColor}' font-size='12' font-weight='500'>${escapeRoleSvgText(displayRole)}</text>`
    : '';
  const avatarCenterX = ROLE_AVATAR_LEFT + ROLE_AVATAR_SIZE / 2;
  const avatarSvg = hideDefaultAvatar
    ? ''
    : makeRoleAvatarSvgMarkup(
        avatarColor,
        '#ffffff',
        avatarCenterX,
        height / 2,
        15
      );
  const svg = `<?xml version="1.0" encoding="UTF-8"?><!DOCTYPE svg>
  <svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}' viewBox='0 0 ${width} ${height}'>
    ${avatarSvg}
    <g font-family='system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif'>
      <text x='58' y='${nameY}' fill='${nameColor}' font-size='${status === 'filled' ? 15 : 14}' font-weight='${status === 'filled' ? 700 : 600}'>${escapeRoleSvgText(displayName)}</text>
      ${roleSvg}
    </g>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}

/** 角色卡片头像，和组织架构人员卡片保持相近视觉语言。 */
function makeRoleAvatarSvgMarkup(
  color: string,
  bg: string,
  centerX: number,
  centerY: number,
  radius: number
): string {
  const headY = centerY - 2;
  const bodyTopY = centerY + 5;
  return `
    <circle cx='${centerX}' cy='${centerY}' r='${radius}' fill='${bg}' stroke='${color}' stroke-width='1.8'/>
    <circle cx='${centerX}' cy='${headY}' r='4.3' fill='none' stroke='${color}' stroke-width='1.7'/>
    <path d='M${centerX - 8.5} ${bodyTopY + 7}c1.6-3.8 4.8-5.8 8.5-5.8s6.9 2 8.5 5.8' fill='none' stroke='${color}' stroke-width='1.7' stroke-linecap='round'/>
  `;
}

/**
 * 已匹配人员卡片使用独立浅色系。
 *
 * 原则：
 * - 不直接复用过程节点主色，避免人员卡片和过程节点混成一片
 * - 保留轻微分支差异，但整体降低饱和度和透明度，让右侧人员信息更轻
 */
function getAgentAccentColor(node: any): string {
  const themeId = (node.data('themeId') as string) || '';
  const palette: Record<string, string> = {
    red: '#fda4af',
    green: '#6ee7b7',
    orange: '#fcd34d',
    purple: '#c4b5fd',
    blue: '#93c5fd',
    pink: '#f9a8d4',
    cyan: '#67e8f9',
  };
  return palette[themeId] || '#a5b4fc';
}

/** 给已匹配人员卡片使用更浅的独立背景色，和过程节点的浅底形成色差。 */
function makeAgentSoftBg(hex: string, alpha = 0.08): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return '#f8fafc';
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** 把主题色转换为透明 rgba，供 DOM overlay 使用。 */
function hexWithAlpha(hex: string, alpha: number): string {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return hex;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** 生成贴着过程节点边缘的椭圆轨迹，让绿色点严格沿节点本体转动。 */
function buildOrbitPath(
  nodeWidth: number,
  nodeHeight: number,
  inset: number
): string {
  const rx = nodeWidth / 2;
  const ry = nodeHeight / 2;
  const cx = inset + rx;
  const cy = inset + ry;
  const startX = cx;
  const startY = cy - ry;
  return `M ${startX} ${startY} A ${rx} ${ry} 0 1 1 ${cx} ${cy + ry} A ${rx} ${ry} 0 1 1 ${startX} ${startY}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** 简单按中英文近似宽度截断，保证 SVG 文本不压到右边框。 */
function fitRoleTextToLine(
  text: string,
  maxWidth: number,
  fontSize: number
): string {
  const source = text || '';
  let width = 0;
  let result = '';
  for (const char of source) {
    const charWidth = /[\u4e00-\u9fa5]/.test(char) ? fontSize : fontSize * 0.58;
    if (width + charWidth > maxWidth) {
      return result + '…';
    }
    width += charWidth;
    result += char;
  }
  return result;
}

/** 转义 SVG 文本，防止人员名或角色名里的特殊字符破坏背景图。 */
function escapeRoleSvgText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** 根据 Vite basePath 生成 public 静态资源路径。 */
function buildPublicAssetUrl(relativePath: string): string {
  const basePath = import.meta.env.VITE_BASE_PATH || '/';
  const normalizedBasePath =
    basePath === '/' ? '' : basePath.replace(/\/+$/, '');
  return `${normalizedBasePath}/${relativePath.replace(/^\/+/, '')}`;
}

/** 根据姓名生成 demo 头像链接；未命中或开关关闭时返回空数组。 */
function resolvePersonDemoAvatarUrls(name: string, enabled: boolean): string[] {
  if (!enabled) return [];
  const fileName = PERSON_AVATAR_DEMO_FILE_MAP[name.trim()];
  if (!fileName) return [];

  const relativePath = `static-files/testavatar/${fileName}`;
  const withBasePath = buildPublicAssetUrl(relativePath);
  const rootPath = `/${relativePath}`;
  if (withBasePath === rootPath) return [withBasePath];
  return [withBasePath, rootPath];
}

/** 命中接口头像或 demo 头像时，隐藏角色卡片里的默认头像图标（保留占位）。 */
function shouldHideRoleDefaultAvatar(
  nodeData: Record<string, unknown>,
  enablePersonAvatarDemo: boolean
): boolean {
  const personName = String(nodeData.personName || '').trim();
  if (!personName) return false;

  const apiAvatarUrl = String(nodeData.avatarUrl || '').trim();
  if (apiAvatarUrl) return true;

  return (
    resolvePersonDemoAvatarUrls(personName, enablePersonAvatarDemo).length > 0
  );
}

/**
 * cytoscape 样式表：分支主题 + 状态 class 叠加。
 *
 * 重要背景：
 * - cytoscape 的选择器解析对多类组合（`.a.b.c.d.e`）在我们这种场景下**不可靠** —
 *   实测即使类全部匹配，规则也不会生效（log 可见 classes 已有 theme-red +
 *   activated，bg 仍为默认灰）。因此本版本**彻底放弃 theme-X 类选择器**，
 *   全部通过 `data(themeColor)` / `data(themeSoftBg)` 函数映射把颜色取出来。
 * - "激活态"依旧通过 `.activated` 正向类表达，但规则只用 2~3 个 class，
 *   不再堆叠到 4+ 级。
 * - 宽高、颜色、背景图都用函数映射，保证每个节点根据自己的 data 呈现正确样式。
 * - 边线默认统一走中性灰；运行时通过 `applyIncomingEdgeState()` 用 bypass 控制点亮，
 *   避免初始渲染时就提前显示分支主题色。
 */
function buildStylesheet(enablePersonAvatarDemo = true): any[] {
  /** 从节点 data 取主题色，缺省时退回中性灰 */
  const themeColor = (ele: any) => ele.data('themeColor') || '#9ca3af';
  const themeSoftBg = (ele: any) => ele.data('themeSoftBg') || '#f3f4f6';
  /** 每次读取节点时按需生成一份彩色 SVG 卡片 —— 比在样式里写死更灵活 */
  const themeIcon = (ele: any) =>
    makeRoleCardDataUri({
      width: ele.data('nodeWidth') || 210,
      height: ele.data('nodeHeight') || 64,
      name: ele.data('displayName') || ele.data('roleName') || '',
      role: ele.data('displayRole') || '',
      color: ele.hasClass('filled')
        ? getAgentAccentColor(ele)
        : ele.data('themeColor') || '#9ca3af',
      status: ele.hasClass('filled') ? 'filled' : 'activated',
      hideDefaultAvatar: shouldHideRoleDefaultAvatar(
        ele.data(),
        enablePersonAvatarDemo
      ),
    });
  /** 过程节点文字也绘制为 SVG 背景，避免 Cytoscape label 在缩放/暂停恢复后错位。 */
  const processLabelImage = (ele: any) =>
    makeProcessLabelDataUri({
      width: ele.data('nodeWidth') || 240,
      height: ele.data('nodeHeight') || 76,
      label: ele.data('label') || '',
      color: getProcessLabelColor(ele),
      fontSize: ele.data('isRoot') ? 19 : 17,
    });

  return [
    // ============ 节点通用 ============
    // 宽高通过函数读取节点 data，让每个节点按 label 长度自适应
    {
      selector: 'node',
      style: {
        label: 'data(label)',
        'text-wrap': 'wrap',
        'text-max-width': (ele: any) => ele.data('nodeWidth') || 150,
        'text-valign': 'center',
        'text-halign': 'center',
        'font-size': 12,
        'font-family':
          'system-ui, -apple-system, PingFang SC, Microsoft YaHei, sans-serif',
        color: '#9ca3af',
        'background-color': '#f3f4f6',
        'border-width': 1.2,
        'border-color': '#d1d5db',
        width: (ele: any) => ele.data('nodeWidth') || 150,
        height: (ele: any) => ele.data('nodeHeight') || 44,
        shape: 'ellipse',
        // 状态点亮统一走显式动画工具，避免全局 transition 在流式事件下重复触发。
        'transition-property': 'none',
        'transition-duration': 0,
      } as any,
    },

    // ============ 过程节点形态 ============
    // 根节点：椭圆 + 加粗字体；激活时深色实心，默认灰色
    {
      selector: 'node.process-node.root',
      style: {
        label: '',
        'text-opacity': 0,
        shape: 'ellipse',
        'background-image': processLabelImage,
        'background-fit': 'none',
        'background-repeat': 'no-repeat',
        'background-width': ROLE_ICON_SIZE,
        'background-height': ROLE_ICON_SIZE,
        'background-width-relative-to': 'inner',
        'background-height-relative-to': 'inner',
        'background-position-x': ROLE_ICON_LEFT,
        'background-position-y': '50%',
        'background-clip': 'node',
        'background-image-containment': 'inside',
      } as any,
    },
    {
      selector: 'node.process-node.root.activated',
      style: {
        'background-color': '#111827',
        'border-color': '#111827',
        color: '#ffffff',
      } as any,
    },
    // 分组/叶子都是椭圆
    {
      selector: 'node.process-node.group',
      style: {
        label: '',
        'text-opacity': 0,
        shape: 'ellipse',
        'background-image': processLabelImage,
        'background-fit': 'none',
        'background-repeat': 'no-repeat',
        'background-width': ROLE_ICON_SIZE,
        'background-height': ROLE_ICON_SIZE,
        'background-width-relative-to': 'inner',
        'background-height-relative-to': 'inner',
        'background-position-x': ROLE_ICON_LEFT,
        'background-position-y': '50%',
        'background-clip': 'node',
        'background-image-containment': 'inside',
      } as any,
    },
    {
      selector: 'node.process-node.leaf',
      style: {
        label: '',
        'text-opacity': 0,
        shape: 'ellipse',
        'background-image': processLabelImage,
        'background-fit': 'none',
        'background-repeat': 'no-repeat',
        'background-width': ROLE_ICON_SIZE,
        'background-height': ROLE_ICON_SIZE,
        'background-width-relative-to': 'inner',
        'background-height-relative-to': 'inner',
        'background-position-x': ROLE_ICON_LEFT,
        'background-position-y': '50%',
        'background-clip': 'node',
        'background-image-containment': 'inside',
      } as any,
    },

    // 运行中额外加粗边框（主题色由上面的 activated 规则给）
    {
      selector: 'node.process-node.status-running',
      style: {
        'border-width': 2.5,
      } as any,
    },
    // 已完成/已销毁：保持着色不变，销毁阶段不再额外变暗。
    {
      selector: 'node.process-node.status-destroyed',
      style: {
        opacity: 1,
      } as any,
    },

    // ============ 角色节点 ============
    // 基础：白底，灰边，灰色人像图标在左侧，文字偏右；宽度由 nodeWidth 决定
    {
      selector: 'node.role-node',
      style: {
        label: '',
        shape: 'round-rectangle',
        'font-size': 11,
        'background-color': '#ffffff',
        'border-color': '#d1d5db',
        color: '#6b7280',
        // 默认角色卡片；激活后在 activated/filled 状态规则里替换为彩色卡片
        'background-image': (ele: any) =>
          makeRoleCardDataUri({
            width: ele.data('nodeWidth') || 210,
            height: ele.data('nodeHeight') || 64,
            name: ele.data('roleName') || '',
            role: '',
            color: '#9ca3af',
            status: 'pending',
            hideDefaultAvatar: shouldHideRoleDefaultAvatar(
              ele.data(),
              enablePersonAvatarDemo
            ),
          }),
        'background-fit': 'none',
        'background-repeat': 'no-repeat',
        'background-width': ROLE_ICON_SIZE,
        'background-height': ROLE_ICON_SIZE,
        'background-width-relative-to': 'inner',
        'background-height-relative-to': 'inner',
        'background-position-x': ROLE_ICON_LEFT,
        'background-position-y': '50%',
        // 图标本身位于节点内部，按官方建议保持 clip=node，避免放大/缩小时绘制盒异常。
        'background-clip': 'node',
        'background-image-containment': 'inside',
        // 文本已绘制在 SVG 中，这里不再依赖 Cytoscape label 排版。
        'text-margin-x': 0,
        'text-max-width': 0,
      } as any,
    },
    {
      selector: 'node.role-node.filled',
      style: {
        'font-weight': 'bold',
      } as any,
    },

    // ============ 结果节点 ============
    {
      selector: 'node.result-node',
      style: {
        shape: 'round-rectangle',
        'font-size': 11.5,
        color: '#374151',
        'background-color': '#f9fafb',
        'border-color': '#e5e7eb',
        'text-max-width': 232,
        'text-justification': 'left',
        'line-height': 1.22,
      } as any,
    },

    // ============ 边通用 ============
    {
      selector: 'edge',
      style: {
        width: 1.2,
        'line-color': '#cbd5e1',
        'target-arrow-color': '#cbd5e1',
        'target-arrow-shape': 'none',
        'curve-style': 'taxi',
        'taxi-direction': 'rightward',
        'taxi-turn': 30,
        // 边线点亮也由显式动画控制，避免和节点动画叠加闪烁。
        'transition-property': 'none',
        'transition-duration': 0,
      } as any,
    },
    {
      selector: 'edge.process-edge',
      style: {
        width: 1.8,
        'line-color': '#cbd5e1',
        // 设计图里过程节点之间都是直接连线，不走 taxi 折线。
        'curve-style': 'straight',
      } as any,
    },
    {
      selector: 'edge.role-edge',
      style: {
        width: 1.2,
        'line-color': '#d1d5db',
        'curve-style': 'taxi',
        'taxi-direction': 'rightward',
        'taxi-turn': 34,
        label: 'data(lineLabel)',
        'font-size': 10,
        color: '#6b7280',
        'text-background-color': '#ffffff',
        'text-background-opacity': 1,
        'text-background-shape': 'roundrectangle',
        'text-background-padding': 2,
        'text-margin-y': -8,
      } as any,
    },
    {
      selector: 'edge.result-edge',
      style: {
        width: 1.2,
        'line-color': '#d1d5db',
        'curve-style': 'taxi',
        'taxi-direction': 'rightward',
        'taxi-turn': 34,
        label: '执行结果',
        'font-size': 10,
        color: '#6b7280',
        'text-background-color': '#ffffff',
        'text-background-opacity': 1,
        'text-background-shape': 'roundrectangle',
        'text-background-padding': 2,
        // 有执行Agent连线时放下方做上下对称；仅有执行结果时放中间避免“压在下方”违和。
        'text-margin-y': (ele: any) => {
          const source = ele.source();
          if (!source || source.empty()) return 0;
          const roleEdgeCount = source.connectedEdges('edge.role-edge').length;
          return roleEdgeCount > 0 ? 8 : 0;
        },
      } as any,
    },

    // ========================================================
    // 分支主题色规则（数据驱动，放在最后以覆盖通用规则）
    // ========================================================

    // --- 分组节点激活：主题实心 + 白字 ---
    {
      selector: 'node.process-node.group.activated',
      style: {
        'background-color': themeColor,
        'border-color': themeColor,
        color: '#ffffff',
      } as any,
    },
    // --- 叶子节点激活：浅底 + 主题描边/文字 ---
    {
      selector: 'node.process-node.leaf.activated',
      style: {
        'background-color': themeSoftBg,
        'border-color': themeColor,
        color: themeColor,
      } as any,
    },
    // --- 角色节点激活（未填充人员）：白底 + 主题描边/文字 + 彩色图标 ---
    {
      selector: 'node.role-node.activated',
      style: {
        'background-color': '#ffffff',
        'border-color': themeColor,
        color: themeColor,
        'background-image': themeIcon,
        'background-width': ROLE_ICON_SIZE,
        'background-height': ROLE_ICON_SIZE,
        'background-position-x': ROLE_ICON_LEFT,
      } as any,
    },
    // --- 角色节点已填充：主题浅底 + 主题描边/文字 ---
    {
      selector: 'node.role-node.filled',
      style: {
        'background-color': (ele: any) =>
          makeAgentSoftBg(getAgentAccentColor(ele)),
        'border-color': (ele: any) => getAgentAccentColor(ele),
        color: '#1f2937',
        'background-image': themeIcon,
        'background-width': ROLE_ICON_SIZE,
        'background-height': ROLE_ICON_SIZE,
        'background-position-x': ROLE_ICON_LEFT,
        'font-weight': 'bold',
      } as any,
    },
    // --- 结果节点已填充：主题浅底 + 主题描边 ---
    {
      selector: 'node.result-node.filled',
      style: {
        'background-color': themeSoftBg,
        'border-color': themeColor,
      } as any,
    },
  ];
}
